/**

 The navbar is pretty slick right now in that it auto-hides, shrinks, and grows depending on scroll position and direction. It actually completely disappears when the user is scrolling down past the scroll length of the header's height.

 It also has a responsive design that adapts to different screen sizes, showing a full-screen overlay menu on mobile devices and a dropdown menu on larger screens. The links are sorted by their collapse order, allowing for a clean and organized navigation experience.

 */

import { useState, useEffect } from 'react';
import { NavLink } from 'react-router';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import { Button } from '../Button';
import { useOrderRoute } from '../../contexts/OrderContext';

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [maxVisible, setMaxVisible] = useState(6); // default to all visible
  const [isMobile, setIsMobile] = useState(false);
  const orderRoute = useOrderRoute();

  // Note: "Order Now" is keyed by the literal '/order' string in the
  // visibility/collapse logic below — keep it as the key, but route the
  // actual NavLink to `orderRoute` so it follows the kill-switch.
  const links = [
    { to: '/', text: 'Home', collapseOrder: 5 },
    { to: '/about', text: 'About Us', collapseOrder: 3 },
    { to: '/pricing', text: 'Pricing', collapseOrder: 2 },
    { to: '/bidets/recommended', text: 'Recommended Bidets', collapseOrder: 4 },
    { to: '/order', text: 'Order Now', collapseOrder: 1 },
  ];

  // Resolve the actual nav target for each link (lets us redirect 'Order Now'
  // to /ordering-paused when the kill-switch is on without breaking the
  // visibility/collapse logic that keys on the original to value).
  const resolveTo = (to: string) => (to === '/order' ? orderRoute : to);

  // Set maxVisible and isMobile based on window width
  useEffect(() => {
    function handleResize() {
      const w = window.innerWidth;
      setIsMobile(w < 600);
      if (w < 240) setMaxVisible(0); // mobile: all in menu
      else if (w < 500) setMaxVisible(1); // sm
      else if (w < 700) setMaxVisible(2); // sm
      else if (w < 800) setMaxVisible(3); // md
      else if (w < 1024) setMaxVisible(4); // lg
      else setMaxVisible(6); // xl and up
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine which links are visible/collapsed by collapseOrder, but render in original order
  const sortedByCollapse = [...links].sort((a, b) => a.collapseOrder - b.collapseOrder);
  const visibleSet = new Set(sortedByCollapse.slice(0, maxVisible).map((l) => l.to));
  const collapsedSet = new Set(sortedByCollapse.slice(maxVisible).map((l) => l.to));

  return (
    <>
      {/* Collapsed links menu (shows only collapsed links) */}
      {isOpen && collapsedSet.size > 0 && (
        isMobile ? (
          // Mobile: full screen overlay using portal
          createPortal(
            <div className="fixed inset-0 z-[100] bg-white flex flex-col">
              <div className="flex justify-end p-4 pt-8">
                <button onClick={() => setIsOpen(false)} aria-label="Close menu">
                  <FiX className="w-8 h-8" />
                </button>
              </div>
              <nav className="flex flex-col items-end justify-start pt-4 pr-8 space-y-6">
                {links.map((link) => (
                  <NavLink
                    key={link.to}
                    to={resolveTo(link.to)}
                    className={({ isActive }) => `block text-2xl py-2 text-gray-800 hover:text-blue-600${isActive ? ' underline font-bold text-blue-600' : ''}`}
                    onClick={() => setIsOpen(false)}
                    end
                  >
                    {link.text}
                  </NavLink>
                ))}
              </nav>
            </div>,
            document.body,
          )
        ) : null // Only render desktop dropdown inside nav below
      )}

      {/* Main nav menu (always visible, controls visible/collapsed links) */}
      <nav className="flex space-x-6 items-center relative">
        {links.filter((link) => visibleSet.has(link.to)).map((link) => {
          if (link.to === '/order') {
            // Render CTA button for 'Order Now' on desktop/tablet
            return (
              <NavLink key={link.to} to={resolveTo(link.to)} end style={{ textDecoration: 'none' }}>
                <Button cta style={{ minWidth: '120px' }} className="ml-2">
                  {link.text}
                </Button>
              </NavLink>
            );
          }
          return (
            <NavLink
              key={link.to}
              to={resolveTo(link.to)}
              className={({ isActive }) => `text-gray-800 hover:text-blue-600 whitespace-nowrap${isActive ? ' underline font-bold text-blue-600' : ''}`}
              end
            >
              {link.text}
            </NavLink>
          );
        })}
        {collapsedSet.size > 0 && (
          <div className="relative" style={{ marginLeft: 'auto' }}>
            <button
              className="p-2 block"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                {isOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            {/* Desktop: dropdown/drawer anchored to button */}
            {!isMobile && isOpen && (
              <div className="absolute right-0 top-full w-56 bg-white shadow-lg rounded-md z-50 border border-gray-200">
                {/* Removed the extra X button for desktop */}
                <nav className="py-2">
                  {links.filter((link) => collapsedSet.has(link.to)).map((link) => (
                    <NavLink
                      key={link.to}
                      to={resolveTo(link.to)}
                      className={({ isActive }) => `block px-4 py-2 text-lg text-gray-800 hover:text-blue-600${isActive ? ' underline font-bold text-blue-600' : ''}`}
                      onClick={() => setIsOpen(false)}
                      end
                    >
                      {link.text}
                    </NavLink>
                  ))}
                </nav>
              </div>
            )}
          </div>
        )}
      </nav>
    </>
  );
}
